import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast, Toaster } from "sonner";
import {
  Video,
  Play,
  Square,
  Send,
  Loader2,
  CheckCircle2,
  XCircle,
  RefreshCw,
  Clock,
  MessageCircle
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * VideoRequestPage - Public page for clients to record video testimonials
 * Accessed via /video/:token (no auth required)
 */
export default function VideoRequestPage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [requestData, setRequestData] = useState(null);
  
  // Recording state
  const [isRecording, setIsRecording] = useState(false);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [recordedUrl, setRecordedUrl] = useState(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  // Refs
  const videoRef = useRef(null);
  const previewRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  // Fetch request data on mount
  useEffect(() => {
    fetchRequestData();
  }, [token]);

  const fetchRequestData = async () => {
    try {
      const res = await axios.get(`${API}/video-testimonials/public/${token}`);
      setRequestData(res.data);
    } catch (e) {
      const status = e.response?.status;
      if (status === 404) {
        setError("This testimonial request was not found.");
      } else if (status === 410) {
        setError(e.response?.data?.detail || "This request has expired or already been completed.");
      } else {
        setError("Unable to load request. Please try again later.");
      }
    } finally {
      setLoading(false);
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: 1280, height: 720 },
        audio: true
      });
      
      streamRef.current = stream;
      videoRef.current.srcObject = stream;
      
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported('video/webm;codecs=vp9')
          ? 'video/webm;codecs=vp9'
          : 'video/webm'
      });
      
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];
      
      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };
      
      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'video/webm' });
        setRecordedBlob(blob);
        setRecordedUrl(URL.createObjectURL(blob));
        
        // Stop stream tracks
        stream.getTracks().forEach(track => track.stop());
      };
      
      mediaRecorder.start(1000); // Capture every second
      setIsRecording(true);
      setRecordingTime(0);
      
      // Start timer
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
      
    } catch (e) {
      console.error("Error starting recording:", e);
      toast.error("Could not access camera/microphone. Please check permissions.");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    }
  };

  const resetRecording = () => {
    if (recordedUrl) {
      URL.revokeObjectURL(recordedUrl);
    }
    setRecordedBlob(null);
    setRecordedUrl(null);
    setRecordingTime(0);
  };

  const uploadVideo = async () => {
    if (!recordedBlob) return;
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', recordedBlob, 'testimonial.webm');
      
      await axios.post(`${API}/video-testimonials/public/${token}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setSubmitted(true);
      toast.success("Thank you! Your testimonial has been submitted.");
    } catch (e) {
      console.error("Upload error:", e);
      toast.error(e.response?.data?.detail || "Failed to upload video. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 text-indigo-500 animate-spin mx-auto mb-4" />
          <p className="text-indigo-400">Loading request...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-4">
            <XCircle className="w-8 h-8 text-red-400" />
          </div>
          <h1 className="text-xl font-bold text-indigo-500 mb-2">Request Unavailable</h1>
          <p className="text-indigo-400">{error}</p>
        </div>
      </div>
    );
  }

  // Success state
  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-10 h-10 text-green-400" />
          </div>
          <h1 className="text-2xl font-bold text-indigo-500 mb-3">Thank You!</h1>
          <p className="text-indigo-400">
            Your video testimonial has been submitted successfully. We truly appreciate you taking the time to share your experience!
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4 sm:p-6 lg:p-8" data-testid="video-request-page">
      <Toaster position="top-center" richColors />
      
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-2xl bg-indigo-500/20 flex items-center justify-center mx-auto mb-4">
            <Video className="w-8 h-8 text-indigo-400" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-indigo-500 mb-2">
            {requestData?.client_name 
              ? `Hi ${requestData.client_name}!` 
              : "Record Your Testimonial"}
          </h1>
          <p className="text-indigo-400 max-w-lg mx-auto">
            {requestData?.message || "We'd love to hear about your experience working with us!"}
          </p>
        </div>

        {/* Questions */}
        {requestData?.questions?.length > 0 && (
          <div className="bg-slate-800/50 rounded-2xl p-6 mb-8 border border-slate-700/50">
            <h3 className="text-indigo-500 font-medium mb-4 flex items-center gap-2">
              <MessageCircle className="w-5 h-5 text-indigo-400" />
              Feel free to answer these questions:
            </h3>
            <ul className="space-y-3">
              {requestData.questions.map((q, i) => (
                <li key={i} className="flex items-start gap-3 text-slate-400">
                  <span className="w-6 h-6 rounded-full bg-indigo-500/20 text-indigo-400 flex items-center justify-center flex-shrink-0 text-sm font-medium">
                    {i + 1}
                  </span>
                  {q}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Video Area */}
        <div className="bg-slate-800/50 rounded-2xl overflow-hidden border border-slate-700/50">
          {/* Video Preview */}
          <div className="aspect-video bg-black relative">
            {!recordedUrl ? (
              <video
                ref={videoRef}
                autoPlay
                muted
                playsInline
                className="w-full h-full object-cover"
              />
            ) : (
              <video
                ref={previewRef}
                src={recordedUrl}
                controls
                className="w-full h-full object-cover"
              />
            )}
            
            {/* Recording indicator */}
            {isRecording && (
              <div className="absolute top-4 left-4 flex items-center gap-2 bg-red-500 px-3 py-1.5 rounded-full">
                <div className="w-2 h-2 bg-white rounded-full animate-pulse" />
                <span className="text-indigo-500 text-sm font-medium">
                  {formatTime(recordingTime)}
                </span>
              </div>
            )}
            
            {/* Max time warning */}
            {isRecording && recordingTime >= 270 && (
              <div className="absolute top-4 right-4 bg-indigo-500/90 px-3 py-1.5 rounded-full">
                <span className="text-black text-sm font-medium flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  {formatTime(300 - recordingTime)} left
                </span>
              </div>
            )}
          </div>

          {/* Controls */}
          <div className="p-4 sm:p-6 flex flex-col sm:flex-row items-center justify-center gap-4">
            {!recordedUrl ? (
              // Recording controls
              !isRecording ? (
                <button
                  onClick={startRecording}
                  className="flex items-center gap-2 bg-red-500 hover:bg-red-600 text-indigo-500 px-6 py-3 rounded-xl font-medium transition-colors"
                  data-testid="start-recording-btn"
                >
                  <Play className="w-5 h-5" />
                  Start Recording
                </button>
              ) : (
                <button
                  onClick={stopRecording}
                  className="flex items-center gap-2 bg-slate-600 hover:bg-slate-400 text-indigo-500 px-6 py-3 rounded-xl font-medium transition-colors"
                  data-testid="stop-recording-btn"
                >
                  <Square className="w-5 h-5" />
                  Stop Recording
                </button>
              )
            ) : (
              // Review controls
              <>
                <button
                  onClick={resetRecording}
                  className="flex items-center gap-2 bg-slate-700 hover:bg-slate-600 text-white px-5 py-3 rounded-xl font-medium transition-colors"
                  data-testid="retake-btn"
                >
                  <RefreshCw className="w-5 h-5" />
                  Re-record
                </button>
                <button
                  onClick={uploadVideo}
                  disabled={uploading}
                  className="flex items-center gap-2 bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white px-6 py-3 rounded-xl font-medium transition-all disabled:opacity-50"
                  data-testid="submit-btn"
                >
                  {uploading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      Submit Testimonial
                    </>
                  )}
                </button>
              </>
            )}
          </div>
        </div>

        {/* Tips */}
        <div className="mt-6 text-center text-sm text-slate-400">
          <p>Tip: Find a quiet spot with good lighting. Your video can be up to 5 minutes long.</p>
        </div>
      </div>
    </div>
  );
}
